import { forwardRef } from 'react';

import type { DefaultOptionType } from 'antd/es/select';
import { FieldValues } from 'react-hook-form';
import { useGlobal } from 'src/contexts/GlobalContext';

import DrawerSelect from '../DrawerSelect';
import { MobileDrawerSelectTrigger } from '../DrawerSelect/DrawerSelect.styled';
import { StyledSelect } from './CustomSelect.styled';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  field?: FieldValues;
  options: SelectOption[];
  placeholder: string | React.ReactNode;
  disabled?: boolean;
  title: string;
  onOkText: string;
  onOk: (value: string | string[]) => void;
  'data-testid'?: string;
  allowClear?: boolean;
  onClear?: () => void;
  defaultValue?: string;
  mode?: 'tags' | 'default';
  tokenSeparators?: string[];
  loading?: boolean;
  showSearch?: boolean;
  optionFilterProp?: string;
  filterOption?: (input: string, option?: DefaultOptionType) => boolean;
}

export const CustomSelect = forwardRef<HTMLDivElement, CustomSelectProps>((props, ref) => {
  const {
    field,
    options,
    placeholder,
    disabled,
    title,
    onOkText,
    onOk,
    'data-testid': dataTestId,
    allowClear,
    onClear,
    defaultValue,
    mode,
    tokenSeparators,
    loading,
    showSearch,
    optionFilterProp,
    filterOption,
  } = props;
  const { isDesktop } = useGlobal();

  const hasValidOptions = options && options.length > 0;
  const currentValue = field?.value;
  const hasValidValue = hasValidOptions && currentValue && options.some((option) => option.value === currentValue);
  const isEmpty = !currentValue || currentValue === '' || currentValue === null || currentValue === undefined;

  const safeField = hasValidValue ? field : { ...field, value: null };

  const mobileDisplayLabel =
    hasValidValue && options ? options.find((o) => o.value === field?.value)?.label : undefined;

  const handleClear = () => {
    if (onClear) {
      onClear();
    }
  };

  const showSearchProps = showSearch
    ? {
        optionFilterProp: optionFilterProp,
        filterOption: filterOption,
      }
    : undefined;

  return isDesktop ? (
    <StyledSelect
      {...safeField}
      key={`desktop-select-${isEmpty}-${field?.value}`}
      placeholder={placeholder}
      options={options}
      disabled={disabled}
      data-testid={dataTestId}
      allowClear={allowClear}
      onClear={handleClear}
      mode={mode === 'tags' ? 'tags' : undefined}
      tokenSeparators={tokenSeparators}
      loading={loading}
      showSearch={showSearchProps}
    />
  ) : (
    <DrawerSelect
      {...safeField}
      value={safeField?.value ?? null}
      options={options}
      onOk={onOk}
      title={title}
      placeholder={placeholder}
      onOkText={onOkText}
      disabled={disabled}
      ref={ref}
      data-testid={dataTestId}
      allowClear={allowClear}
      onClear={onClear}
      defaultValue={defaultValue}
      mode={mode === 'tags' ? 'tags' : undefined}
      tokenSeparators={tokenSeparators}
      loading={loading}
      showSearch={showSearch}
      triggerComponent={
        <MobileDrawerSelectTrigger
          loading={loading}
          open={false}
          placeholder={placeholder}
          value={mobileDisplayLabel}
          allowClear={allowClear}
        />
      }
    />
  );
});

CustomSelect.displayName = 'CustomSelect';
